/**
 * E-commerce — storefronts, the product feeds that supply them, and the
 * orders that come back.
 *
 * The three screens are one pipeline: a storefront is a sales channel, a feed
 * is what you publish to it, and an order is what returns. The overview says
 * so explicitly, because a storefront with no feed looks configured and sells
 * nothing.
 *
 * A storefront starts in the workspace currency, and an order is shown in its
 * own. The overview's order value is totalled per currency (the workspace
 * currency first, the others beneath it), never added across them (CUR-2).
 */
import { Link } from "react-router-dom";
import { useCurrencyCreateField } from "@/components/CurrencyField";
import { MoneyStatCard } from "@/components/Money";
import { ResourceListPage } from "@/components/ResourceListPage";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  StatusBadge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import {
  sumByCurrency,
  toCurrencyFigure,
  useWorkspaceCurrency,
} from "@/lib/currency";
import { formatDateTime, formatMoney } from "@/lib/money";

export interface StorefrontRow {
  id: string;
  name: string;
  url: string | null;
  currency_code: string;
  status: string;
  created_at: string;
}

export interface ProductFeedRow {
  id: string;
  name: string;
  channel: string;
  status: string;
  created_at: string;
}

export interface EcommerceOrderRow {
  id: string;
  order_number: string;
  storefront_id: string | null;
  customer_id: string | null;
  total: string;
  currency_code: string;
  status: string;
  created_at: string;
}

export function StorefrontsPage() {
  const currencyField = useCurrencyCreateField();
  return (
    <ResourceListPage<StorefrontRow>
      title="Storefronts"
      subtitle="Online sales channels. Each has its own currency and its own order stream."
      breadcrumb={["E-commerce", "Storefronts"]}
      path="/ecommerce/storefronts"
      queryKey={["ecommerce", "storefronts"]}
      createLabel="New storefront"
      emptyTitle="No storefronts"
      emptyMessage="A storefront is a channel you sell through — your own shop, a marketplace, a partner site. Orders arrive against one."
      createFields={[
        {
          field: "name",
          label: "Storefront name",
          required: true,
          placeholder: "Main web shop",
        },
        {
          field: "url",
          label: "URL",
          required: true,
          type: "url",
          placeholder: "https://shop.example.com",
        },
        currencyField,
        {
          field: "status",
          label: "Status",
          options: ["Draft", "Active", "Paused", "Closed"],
        },
      ]}
      columns={[
        { key: "name", label: "Storefront" },
        {
          key: "url",
          label: "URL",
          render: (r) =>
            r.url ? (
              <a href={r.url} target="_blank" rel="noopener noreferrer">
                {r.url}
              </a>
            ) : (
              "—"
            ),
        },
        {
          key: "currency_code",
          label: "Currency",
          render: (r) => <span className="atlas-mono">{r.currency_code}</span>,
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
      ]}
    />
  );
}

export function ProductFeedsPage() {
  return (
    <ResourceListPage<ProductFeedRow>
      title="Product feeds"
      subtitle="What gets published to each channel, and whether the last publish succeeded."
      breadcrumb={["E-commerce", "Product feeds"]}
      path="/ecommerce/product-feeds"
      queryKey={["ecommerce", "product-feeds"]}
      createLabel="New feed"
      emptyTitle="No product feeds"
      emptyMessage="A feed is the catalogue you push to a channel. Without one a storefront has nothing to sell, however it is configured."
      createFields={[
        {
          field: "name",
          label: "Feed name",
          required: true,
          placeholder: "Full catalogue",
        },
        {
          field: "channel",
          label: "Channel",
          required: true,
          options: [
            "Shopify",
            "WooCommerce",
            "Amazon",
            "eBay",
            "Google Shopping",
            "Meta",
            "Custom",
          ],
        },
        {
          field: "status",
          label: "Status",
          options: ["Draft", "Active", "Paused"],
        },
      ]}
      columns={[
        { key: "name", label: "Feed" },
        { key: "channel", label: "Channel" },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "created_at",
          label: "Created",
          render: (r) => formatDateTime(r.created_at),
        },
      ]}
    />
  );
}

export function EcommerceOrdersPage() {
  const storefronts = useApiList<StorefrontRow>(
    ["ecommerce", "storefronts"],
    "/ecommerce/storefronts",
  );

  return (
    <ResourceListPage<EcommerceOrderRow>
      title="Online orders"
      subtitle="Orders received from a storefront, before they become sales orders in the ledger."
      breadcrumb={["E-commerce", "Orders"]}
      path="/ecommerce/orders"
      queryKey={["ecommerce", "orders"]}
      emptyTitle="No online orders"
      emptyMessage="Orders arrive from a connected storefront. They are captured here first, then become sales orders once accepted."
      columns={[
        {
          key: "order_number",
          label: "Order",
          render: (r) => <span className="atlas-mono">{r.order_number}</span>,
        },
        {
          key: "storefront_id",
          label: "Storefront",
          render: (r) =>
            storefronts.data?.items.find((s) => s.id === r.storefront_id)
              ?.name ?? "—",
        },
        {
          key: "total",
          label: "Total",
          numeric: true,
          render: (r) => formatMoney(r.total, r.currency_code),
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "created_at",
          label: "Received",
          render: (r) => formatDateTime(r.created_at),
        },
      ]}
    />
  );
}

export function EcommercePage() {
  const storefronts = useApiList<StorefrontRow>(
    ["ecommerce", "storefronts"],
    "/ecommerce/storefronts",
  );
  const feeds = useApiList<ProductFeedRow>(
    ["ecommerce", "product-feeds"],
    "/ecommerce/product-feeds",
  );
  const orders = useApiList<EcommerceOrderRow>(
    ["ecommerce", "orders"],
    "/ecommerce/orders",
  );

  const workspaceCurrency = useWorkspaceCurrency();

  if (storefronts.isLoading) return <Skeleton rows={8} />;

  const storefrontRows = storefronts.data?.items ?? [];
  const orderRows = orders.data?.items ?? [];
  // Per currency, exactly, over the orders loaded; never one sum labelled
  // with one currency.
  const orderValue = workspaceCurrency
    ? toCurrencyFigure(
        sumByCurrency(
          orderRows.map((o) => ({
            amount: o.total,
            currency: o.currency_code,
          })),
        ),
        workspaceCurrency,
      )
    : undefined;
  const activeFeeds = (feeds.data?.items ?? []).filter(
    (f) => f.status === "Active",
  ).length;

  return (
    <div>
      <PageHeader
        title="E-commerce"
        subtitle="Channels you sell through, what you publish to them, and what comes back."
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <StatCard label="Storefronts" value={storefronts.data?.total ?? 0} />
        <StatCard
          label="Active feeds"
          value={activeFeeds}
          meta={
            storefrontRows.length > 0 && activeFeeds === 0
              ? "Storefronts have nothing to sell"
              : undefined
          }
          tone={
            storefrontRows.length > 0 && activeFeeds === 0
              ? "negative"
              : undefined
          }
        />
        <StatCard label="Orders" value={orders.data?.total ?? 0} />
        {orderValue ? (
          <MoneyStatCard label="Order value" figure={orderValue} />
        ) : (
          <StatCard label="Order value" value="—" />
        )}
      </div>

      {storefrontRows.length === 0 ? (
        <EmptyState
          title="No storefront configured"
          message="Start with a storefront — it is the channel orders arrive against. Then publish a product feed to it, or nothing will be listed for sale."
          action={
            <Link to="/app/ecommerce/storefronts">
              <span className="atlas-btn atlas-btn--primary">
                Add a storefront
              </span>
            </Link>
          }
        />
      ) : (
        <Card title="Recent orders">
          {orderRows.length === 0 ? (
            <EmptyState
              title="No orders yet"
              message={
                activeFeeds === 0
                  ? "No product feed is active, so the storefront has nothing listed. Publish a feed first."
                  : "Orders will appear here as the storefront receives them."
              }
            />
          ) : (
            <DataTable<EcommerceOrderRow>
              rows={orderRows.slice(0, 10)}
              columns={[
                {
                  key: "order_number",
                  label: "Order",
                  render: (r) => (
                    <span className="atlas-mono">{r.order_number}</span>
                  ),
                },
                {
                  key: "storefront_id",
                  label: "Storefront",
                  render: (r) =>
                    storefrontRows.find((s) => s.id === r.storefront_id)
                      ?.name ?? "—",
                },
                {
                  key: "total",
                  label: "Total",
                  numeric: true,
                  render: (r) => formatMoney(r.total, r.currency_code),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (r) => <StatusBadge status={r.status} />,
                },
              ]}
            />
          )}
        </Card>
      )}
    </div>
  );
}
